import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Wrench, ScanLine, Play, Undo2, Loader2, AlertTriangle,
  CheckCircle2, ChevronDown, ChevronRight, History,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../ui/dialog";
import { API } from "../../config/api";

const fmt = (n) => (n ?? 0).toLocaleString("en-IN");

const StatLine = ({ label, value, tone = "slate" }) => (
  <div className="flex items-center justify-between text-sm py-1">
    <span className="text-slate-600">{label}</span>
    <Badge variant="outline" className={`font-mono bg-${tone}-50 text-${tone}-800 border-${tone}-200`}>{fmt(value)}</Badge>
  </div>
);

export default function ProjectIdBackfillCard() {
  const [dryrun, setDryrun] = useState(null);
  const [loadingDry, setLoadingDry] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [showApplyConfirm, setShowApplyConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [runs, setRuns] = useState([]);
  const [showRuns, setShowRuns] = useState(false);
  const [rollingBack, setRollingBack] = useState(null); // run_id being rolled back
  const [showRollbackConfirm, setShowRollbackConfirm] = useState(null); // run object
  const [rollbackText, setRollbackText] = useState("");

  const loadRuns = async () => {
    try {
      const res = await axios.get(`${API}/admin/project-id-backfill/runs?limit=20`);
      setRuns(res.data.runs || []);
    } catch (e) {
      // Non-fatal
    }
  };

  useEffect(() => { loadRuns(); }, []);

  const runDryRun = async () => {
    setLoadingDry(true);
    setDryrun(null);
    setApplyResult(null);
    try {
      const res = await axios.post(`${API}/admin/project-id-backfill/dry-run`);
      setDryrun(res.data);
      toast.success(`Dry-run complete — ${fmt(res.data.totals.resolvable)} record(s) to update`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Dry-run failed");
    } finally {
      setLoadingDry(false);
    }
  };

  const runApply = async () => {
    setApplying(true);
    setShowApplyConfirm(false);
    setConfirmText("");
    try {
      const res = await axios.post(`${API}/admin/project-id-backfill/apply?confirm=CONFIRM`);
      setApplyResult(res.data);
      toast.success(`Apply complete — ${fmt(res.data.totals.updated)} updated`);
      setDryrun(null);
      loadRuns();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Apply failed");
    } finally {
      setApplying(false);
    }
  };

  const runRollback = async (run) => {
    setRollingBack(run.run_id);
    setShowRollbackConfirm(null);
    setRollbackText("");
    try {
      const res = await axios.post(
        `${API}/admin/project-id-backfill/rollback?run_id=${run.run_id}&confirm=ROLLBACK`
      );
      toast.success(`Rolled back — ${fmt(res.data.totals.reverted)} reverted`);
      loadRuns();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rollback failed");
    } finally {
      setRollingBack(null);
    }
  };

  const t = dryrun?.totals;
  const resolvable = t?.resolvable ?? 0;
  const unresolvable = t?.unresolvable ?? 0;
  const samples = dryrun?.samples || [];

  return (
    <Card className="border-amber-200" data-testid="project-id-backfill-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Wrench className="w-4 h-4 text-amber-600" />
          Project-ID → Name Backfill
          <Badge className="bg-amber-100 text-amber-800 border-amber-200">Hot fix</Badge>
        </CardTitle>
        <p className="text-xs text-slate-600 mt-1">
          Fixes the Feb 2026 import bug where <code className="bg-slate-100 px-1">leads.project_interest</code> was
          stored as the project ID (<code>proj_xxx</code>) instead of the name (<code>Highpark</code>).
          Touches <strong>leads.project_interest</strong>, <strong>site_visits.project</strong>,
          <strong> site_visits.project_interest</strong>, and <strong>tasks.project_interest</strong> only when
          the current value matches <code>^proj_</code>. Idempotent. Audited. Reversible.
        </p>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Action buttons */}
        <div className="flex flex-wrap items-center gap-2">
          <Button
            onClick={runDryRun}
            disabled={loadingDry || applying}
            data-testid="dryrun-project-id-backfill"
            className="bg-sky-600 hover:bg-sky-700"
          >
            {loadingDry ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ScanLine className="w-4 h-4 mr-2" />}
            Dry Run
          </Button>
          <Button
            onClick={() => setShowApplyConfirm(true)}
            disabled={!dryrun || resolvable === 0 || applying}
            data-testid="apply-project-id-backfill"
            className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40"
          >
            {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
            Apply ({fmt(resolvable)})
          </Button>
          <Button
            variant="outline"
            onClick={() => { setShowRuns(!showRuns); loadRuns(); }}
            data-testid="show-runs-project-id-backfill"
          >
            <History className="w-4 h-4 mr-2" />
            {showRuns ? "Hide" : "Show"} runs ({runs.length})
          </Button>
        </div>

        {/* Dry-run summary */}
        {dryrun && (
          <div className="border border-sky-200 bg-sky-50 rounded-lg p-3" data-testid="dryrun-result">
            <p className="text-sm font-semibold text-sky-900 mb-2">Dry-run summary</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
              <StatLine label="Matched" value={t.matched} tone="sky" />
              <StatLine label="Resolvable" value={t.resolvable} tone="emerald" />
              <StatLine label="Unresolvable" value={t.unresolvable} tone={t.unresolvable > 0 ? "rose" : "slate"} />
              <StatLine label="Chunks (500/op)" value={t.chunks} tone="slate" />
            </div>

            <p className="text-xs font-semibold uppercase tracking-wide text-sky-800 mt-3 mb-1">By collection</p>
            <div className="space-y-1">
              {Object.entries(dryrun.breakdown_by_collection).map(([k, v]) => (
                <div key={k} className="grid grid-cols-4 gap-2 text-xs">
                  <code className="text-slate-700">{k}</code>
                  <span>matched <strong>{fmt(v.matched)}</strong></span>
                  <span>resolvable <strong className="text-emerald-700">{fmt(v.resolvable)}</strong></span>
                  <span>unresolvable <strong className={v.unresolvable > 0 ? "text-rose-700" : ""}>{fmt(v.unresolvable)}</strong></span>
                </div>
              ))}
            </div>

            <p className="text-xs font-semibold uppercase tracking-wide text-sky-800 mt-3 mb-1">By project</p>
            {dryrun.breakdown_by_project.length === 0 ? (
              <p className="text-xs text-slate-500 italic">Nothing to do — all values already names.</p>
            ) : (
              <div className="space-y-1">
                {dryrun.breakdown_by_project.map((p) => (
                  <div key={p.project_id} className="flex items-center gap-2 text-xs">
                    <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">{p.name}</Badge>
                    <code className="text-slate-500">{p.project_id}</code>
                    <span>→ <strong>{fmt(p.total)}</strong> records</span>
                  </div>
                ))}
              </div>
            )}

            {dryrun.unresolved_ids.length > 0 && (
              <div className="mt-3 bg-rose-50 border border-rose-200 rounded p-2">
                <p className="text-xs font-semibold text-rose-800 flex items-center gap-1">
                  <AlertTriangle className="w-3.5 h-3.5" /> Unresolved IDs (will be skipped — no matching project in DB)
                </p>
                <div className="mt-1 space-y-0.5">
                  {dryrun.unresolved_ids.map((u) => (
                    <div key={u.project_id} className="text-xs text-rose-700">
                      <code>{u.project_id}</code> — {fmt(u.count)} records
                    </div>
                  ))}
                </div>
              </div>
            )}

            {samples.length > 0 && (
              <details className="mt-3" open>
                <summary className="text-xs font-semibold uppercase tracking-wide text-sky-800 cursor-pointer">
                  Sample (first {samples.length}) — before → after
                </summary>
                <div className="mt-2 overflow-x-auto">
                  <table className="text-xs w-full">
                    <thead className="text-slate-500">
                      <tr>
                        <th className="text-left pr-2">Lead</th>
                        <th className="text-left pr-2">Source</th>
                        <th className="text-left pr-2">Before</th>
                        <th className="text-left pr-2">After</th>
                      </tr>
                    </thead>
                    <tbody>
                      {samples.map((s, i) => (
                        <tr key={i} className="border-t border-sky-100">
                          <td className="pr-2 py-1">
                            <p className="font-medium">{s.lead_name}</p>
                            <p className="text-[10px] text-slate-500">{s.lead_id}</p>
                          </td>
                          <td className="pr-2 py-1">{s.source || "—"}</td>
                          <td className="pr-2 py-1"><code className="text-rose-700">{s.before}</code></td>
                          <td className="pr-2 py-1">
                            <code className={s.will_update ? "text-emerald-700" : "text-slate-500"}>{s.after}</code>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}

            <p className="text-[11px] text-slate-500 mt-3">
              Scanned at {dryrun.scanned_at?.slice(0, 19).replace("T", " ")} UTC ·
              Pattern: <code>{dryrun.guardrails.match_pattern}</code> ·
              Values not matching this pattern are <strong>never</strong> touched.
            </p>
          </div>
        )}

        {/* Apply result */}
        {applyResult && (
          <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-3" data-testid="apply-result">
            <p className="text-sm font-semibold text-emerald-900 mb-2 flex items-center gap-1">
              <CheckCircle2 className="w-4 h-4" /> Apply complete
            </p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              <StatLine label="Matched" value={applyResult.totals.matched} />
              <StatLine label="Updated" value={applyResult.totals.updated} tone="emerald" />
              <StatLine label="Unresolved" value={applyResult.totals.skipped_unresolved} tone={applyResult.totals.skipped_unresolved > 0 ? "rose" : "slate"} />
              <StatLine label="Unexpected" value={applyResult.totals.skipped_unexpected} />
              <StatLine label="Errors" value={applyResult.totals.errors} tone={applyResult.totals.errors > 0 ? "rose" : "slate"} />
            </div>
            <p className="text-[11px] text-slate-500 mt-2">
              run_id <code>{applyResult.run_id}</code> · {applyResult.chunks_count} chunks · rollback available below.
            </p>
          </div>
        )}

        {/* Run history */}
        {showRuns && (
          <div className="border border-slate-200 rounded-lg p-3" data-testid="runs-history">
            <p className="text-sm font-semibold text-slate-800 mb-2">Run history (most recent first)</p>
            {runs.length === 0 ? (
              <p className="text-xs text-slate-500 italic">No runs yet.</p>
            ) : (
              <div className="space-y-1">
                {runs.map((r) => {
                  const isApply = r.kind === "apply";
                  const totals = r.totals || {};
                  return (
                    <div key={r.run_id} className="border border-slate-100 rounded p-2 text-xs flex flex-wrap items-center gap-2" data-testid={`run-row-${r.run_id}`}>
                      <Badge className={isApply ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}>
                        {r.kind}
                      </Badge>
                      <code className="text-slate-600">{r.run_id}</code>
                      <span className="text-slate-500">{r.started_at?.slice(0, 19).replace("T", " ")} UTC</span>
                      <span>by <strong>{r.performed_by_name || r.performed_by}</strong></span>
                      <span className="ml-auto text-slate-700">
                        {isApply
                          ? <>updated <strong>{fmt(totals.updated)}</strong> · unresolved {fmt(totals.skipped_unresolved)} · errors {fmt(totals.errors)}</>
                          : <>reverted <strong>{fmt(totals.reverted)}</strong> · skipped {fmt(totals.skipped_modified_since)} · errors {fmt(totals.errors)}</>}
                      </span>
                      {isApply && totals.updated > 0 && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setShowRollbackConfirm(r)}
                          disabled={rollingBack === r.run_id}
                          data-testid={`rollback-${r.run_id}`}
                          className="text-amber-700 border-amber-300 hover:bg-amber-50"
                        >
                          {rollingBack === r.run_id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Undo2 className="w-3 h-3 mr-1" />}
                          Rollback
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </CardContent>

      {/* Apply confirm dialog */}
      <Dialog open={showApplyConfirm} onOpenChange={(o) => !o && setShowApplyConfirm(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              Confirm Apply
            </DialogTitle>
            <DialogDescription>
              About to update <strong>{fmt(resolvable)}</strong> records across leads / site_visits / tasks.
              A full rollback map will be saved so you can reverse this run.
              Type <code className="bg-slate-100 px-1">CONFIRM</code> below to proceed.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Type CONFIRM"
            data-testid="apply-confirm-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowApplyConfirm(false); setConfirmText(""); }}>Cancel</Button>
            <Button
              onClick={runApply}
              disabled={confirmText !== "CONFIRM"}
              data-testid="apply-confirm-submit"
              className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40"
            >
              Apply now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rollback confirm dialog */}
      <Dialog open={!!showRollbackConfirm} onOpenChange={(o) => !o && setShowRollbackConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Undo2 className="w-5 h-5 text-amber-600" />
              Confirm Rollback
            </DialogTitle>
            <DialogDescription>
              About to revert run <code>{showRollbackConfirm?.run_id}</code> —
              <strong> {fmt(showRollbackConfirm?.totals?.updated)}</strong> records will go back to their
              original <code>proj_*</code> ID values. Records modified by other users since the apply
              will be left alone. Type <code className="bg-slate-100 px-1">ROLLBACK</code> below.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={rollbackText}
            onChange={(e) => setRollbackText(e.target.value)}
            placeholder="Type ROLLBACK"
            data-testid="rollback-confirm-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowRollbackConfirm(null); setRollbackText(""); }}>Cancel</Button>
            <Button
              onClick={() => runRollback(showRollbackConfirm)}
              disabled={rollbackText !== "ROLLBACK"}
              data-testid="rollback-confirm-submit"
              className="bg-amber-600 hover:bg-amber-700 disabled:opacity-40"
            >
              Rollback now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
